import React from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Fade from "@mui/material/Fade";
import MDSnackbar from "components/MDSnackbar";
import { useState } from "react";
import { FormControl, InputLabel } from "@mui/material";
import appData from "layouts/categories/data/appData";
import { useAddCategory, useRefetchCategories } from "api/useCategories.ts";
import MDBox from "components/MDBox";
import { useMediaQuery, useTheme } from "@mui/material";
import { Box } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CircularProgress from "@mui/material/CircularProgress";

function Tables() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const addCategory = useAddCategory();
  const { refetch } = useRefetchCategories();
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: {
      ar: "",
      en: "",
    },
    description: {
      ar: "",
      en: "",
    },
    image: "https://res.cloudinary.com/dxaxyz62w/image/upload/v1740483339/hajj_x6ujps.png",
    icon: "https://res.cloudinary.com/dxaxyz62w/image/upload/v1740483339/hajj_x6ujps.png",
    video:
      "https://res.cloudinary.com/dgljetjdr/video/upload/v1740588185/Screen_Recording_2025-02-26_163931_dw5jma.mp4",
  });

  const {
    columns,
    rows,
    editedCategory,
    handleCloseModal,
    isModalOpen,
    notification,
    setNotification,
    handleSaveChanges,
    handleCloseNotification,
    handleInputChange,
    handleDeleteCategory,
    isDeleteModalOpen,
    closeDeleteModal,
    handleUploadImage: originalHandleUploadImage,
    handleFileChange,
    isFileModalOpen,
    closeFileModal,
    imagePreview,
    setImagePreview,
    isIcon,
  } = appData();

  // Modified upload handler with loading state
  const handleUploadImage = async () => {
    setIsUploading(true);
    try {
      await originalHandleUploadImage();
      // Reset image preview on successful upload
      setImagePreview(null);
    } catch (error) {
      console.error("Error uploading image:", error);
      setNotification({
        open: true,
        message: "Failed to upload image. Please try again.",
        severity: "error",
      });
    } finally {
      setIsUploading(false);
      refetch(); // Refetch categories after upload
    }
  };

  const handleAddCategoryClick = () => {
    setIsCategoryModalOpen(true); // Open modal for adding new category
  };

  const handleCloseAddCategoryModal = () => {
    setIsCategoryModalOpen(false); // Close modal
    setNewCategory({
      name: {
        en: "",
        ar: "",
      },
      description: {
        en: "",
        ar: "",
      },
    });
  };

  const handleInputChangeNewCategory = (e) => {
    const { name, value } = e.target;

    // Check if the input is for a nested property (e.g., name.en)
    if (name.includes(".")) {
      const [parentKey, childKey] = name.split(".");
      setNewCategory((prevState) => ({
        ...prevState,
        [parentKey]: {
          ...prevState[parentKey],
          [childKey]: value,
        },
      }));
    } else {
      // For non-nested fields
      setNewCategory((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const handleSaveCategory = async () => {
    const categoryData = {
      name: {
        en: newCategory.name.en,
        ar: newCategory.name.ar,
      },
      description: {
        en: newCategory.description.en,
        ar: newCategory.description.ar,
      },
      image: newCategory.image,
      icon: newCategory.icon,
      video: newCategory.video,
    };

    addCategory.mutate(categoryData, {
      onSuccess: () => {
        setNotification({
          open: true,
          message: "Category added successfully",
          severity: "success",
        });
        handleCloseAddCategoryModal(); // Close modal on success
      },
      onError: (error) => {
        console.error("Error adding category:", error);
        setNotification({
          open: true,
          message: "Failed to add category. Please try again.",
          severity: "error",
        });
      },
    });
  };
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="dark"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                justifyContent="space-between"
                alignItems={isMobile ? "flex-start" : "center"}
              >
                <MDTypography variant="h6" color="white">
                  Categories Table
                </MDTypography>
                <MDBox
                  display="flex"
                  flexDirection={isMobile ? "column" : "row"}
                  gap={isMobile ? 2 : "10px"}
                  width={isMobile ? "100%" : "auto"}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleAddCategoryClick}
                    fullWidth={isMobile}
                    sx={{
                      borderRadius: 2,
                      fontSize: 14,
                      padding: "8px 16px",
                      color: "black",
                    }}
                  >
                    Add Category
                  </Button>
                </MDBox>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={true}
                  entriesPerPage={false}
                  showTotalEntries
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      {/* Category Modal */}
      <Dialog open={isCategoryModalOpen} onClose={handleCloseAddCategoryModal}>
        <DialogTitle>Add New Category</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {/* Category Name (English) */}
              <TextField
                name="name.en"
                label="Category Name (English)"
                value={newCategory.name.en}
                onChange={handleInputChangeNewCategory}
                fullWidth
                margin="dense"
              />
            </Grid>
            <Grid item xs={6}>
              {/* Category Name (Arabic) */}
              <TextField
                name="name.ar"
                label="Category Name (Arabic)"
                value={newCategory.name.ar}
                onChange={handleInputChangeNewCategory}
                fullWidth
                margin="dense"
                dir="rtl" // Right-to-left direction for Arabic
              />
            </Grid>
          </Grid>
          {/* Description (English) */}
          <TextField
            name="description.en"
            label="Description (English)"
            value={newCategory.description.en}
            onChange={handleInputChangeNewCategory}
            fullWidth
            margin="dense"
            multiline
            rows={4}
          />
          {/* Description (Arabic) */}
          <TextField
            name="description.ar"
            label="Description (Arabic)"
            value={newCategory.description.ar}
            onChange={handleInputChangeNewCategory}
            fullWidth
            margin="dense"
            multiline
            rows={4}
            dir="rtl" // Right-to-left direction for Arabic
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddCategoryModal} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSaveCategory} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit Category Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 500 }}
      >
        <DialogTitle>Edit Category Details</DialogTitle>
        {editedCategory && (
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  name="name.en"
                  label="Category Name (English)"
                  value={editedCategory.name.en}
                  onChange={handleInputChange}
                  fullWidth
                  margin="dense"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="name.ar"
                  label="Category Name (Arabic)"
                  value={editedCategory.name.ar}
                  onChange={handleInputChange}
                  fullWidth
                  margin="dense"
                  dir="rtl"
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  name="description.en"
                  label="Description (English)"
                  value={editedCategory.description.en}
                  onChange={handleInputChange}
                  fullWidth
                  margin="dense"
                  multiline
                  rows={4}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="description.ar"
                  label="Description (Arabic)"
                  value={editedCategory.description.ar}
                  onChange={handleInputChange}
                  fullWidth
                  margin="dense"
                  multiline
                  rows={4}
                  dir="rtl"
                />
              </Grid>
            </Grid>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Cancel
          </Button>
          <Button onClick={() => handleSaveChanges(editedCategory)} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirm Deletion */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={closeDeleteModal}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 500 }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <MDTypography variant="body1" color="textSecondary">
            Are you sure you want to delete this Category?
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteModal} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleDeleteCategory} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      {/* File Modal */}
      <Dialog
        open={isFileModalOpen}
        onClose={closeFileModal}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 500 }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Category {isIcon ? "Icon" : "Image"}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            {/* Image Preview */}
            {imagePreview && (
              <Box
                component="img"
                src={imagePreview}
                alt="Category Preview"
                sx={{
                  width: "100%",
                  height: "auto",
                  maxHeight: 300,
                  borderRadius: 2,
                  objectFit: "contain",
                  border: "1px solid #ccc",
                }}
              />
            )}
            <Button
              component="label"
              tabIndex={-1}
              startIcon={<CloudUploadIcon />}
              fullWidth
              disabled={isUploading}
            >
              Upload Category {isIcon ? "Icon" : "Image"}
              <input
                hidden
                accept="image/*"
                type="file"
                name="image"
                onChange={handleFileChange}
                disabled={isUploading}
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFileModal} color="secondary" disabled={isUploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUploadImage}
            color="primary"
            disabled={isUploading || !imagePreview}
            startIcon={isUploading ? <CircularProgress size={20} /> : null}
          >
            {isUploading ? "Uploading..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <MDSnackbar
        color={notification.severity}
        icon={notification.severity === "success" ? "check" : "error"}
        title={notification.severity === "success" ? "Success" : "Error"}
        content={notification.message}
        open={notification.open}
        onClose={handleCloseNotification}
        close={handleCloseNotification}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default Tables;
